import Typography from '@mui/material/Typography'
import { Edit } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import TextInput from 'components/molecules/TextInput/TextInput'
import AutocompleteInput from '../AutocompleteInput/AutocompleteInput'
import { ImplantReportSenderModal } from './ImplantReportSenderModal/ImplantReportSenderModal'
import { LoadingButton } from 'components/mui'
import { CustomModal } from 'components/molecules/Modal/Modal'
import { ViewImplantReportModal } from './ViewImplantReportModal/ViewImplantReportModal'
import { EPhysiciansTitle, ImplantReportType } from 'common/types'
import { ImplantReportSenderProps } from './ImplantReportSender.types'
import { useImplantReportSenderLogic } from './ImplantReportSender.logic'
import './ImplantReportSender.scss'
import classNames from 'classnames'

export const ImplantReportSender = (props: ImplantReportSenderProps) => {
  const { surgeryId, implantReportStatus, surgeryDate, provider } = props
  const logic = useImplantReportSenderLogic(props)

  return (
    <div className="implant-report-sender-container">
      <div className="implant-report-header">
        <div className="implant-header-title">
          <Typography variant="h3" sx={{ transform: 'none' }}>
            Share Surgery Report(s)
          </Typography>
          <Typography variant="body2" className="secondary-text">
            {logic.shareSurgeryReportSubtitle}
          </Typography>
        </div>

        {logic.isReportSent && (
          <LoadingButton
            fullwidth={false}
            wrapText
            onClick={() => {
              logic.setViewReportsModalOpen(true)
            }}
            disabled={!implantReportStatus || implantReportStatus.length === 0}
          >
            View Report(s)
          </LoadingButton>
        )}
      </div>

      <div
        className={classNames('referring-input', {
          'is-mobile': logic.isMobile,
        })}
      >
        <AutocompleteInput
          id="referringLastName"
          label="Physician Last Name"
          name="referringLastName"
          variant="outlined"
          value={logic.selectedPhysician}
          onTextInputChange={(e) => logic.setSearch(e.target.value as string)}
          handleChangeFormData={logic.handleSelectPhysician}
          fullWidth
          autoSelect={false}
          showEmail
          options={logic.lastNameOptions}
          isLoading={logic.physicianLoading}
          addOption
          disableClearable
          isConsumablesOptions={false}
          noOptionsText={
            logic.lastNameOptions.length === 0 &&
            logic.search.length > 1 &&
            !logic.physicianLoading ? (
              <div className="implant-report-header-no-options">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No options found
                </Typography>
                <LoadingButton onClick={logic.handleAddPhysicianClick}>
                  {logic.physiciansTitle ===
                  EPhysiciansTitle.ReferringPhysicians
                    ? 'Add Referring Physician'
                    : 'Add Physician'}
                </LoadingButton>
              </div>
            ) : (
              'Please enter a last name to begin search'
            )
          }
        />
        <div className="physician-container">
          <TextInput
            id="referringEmail"
            label="Physician Email"
            variant="outlined"
            value={logic.physicianDetails?.email}
            fullWidth
            disabled
          />
          {logic.physicianDetails?.email &&
            logic.physicianDetails.email !== provider?.emailAddresses[0] && (
              <IconButton onClick={logic.handleEditClick}>
                <Edit />
              </IconButton>
            )}
        </div>
      </div>

      {logic.reports && (
        <AutocompleteInput
          id={'implant-report-input'}
          className="implant-report-menu"
          handleChangeFormData={(e) => {
            const value = logic.unFormatEnumValue(e.target.value as string)

            logic.setImplantReportInputs((prevState) => ({
              ...prevState,
              reportType: value,
            }))
            logic.setInputModalOpen(true)
          }}
          name={'implant-report'}
          label="Select Report Template"
          variant="outlined"
          value={logic.formatEnumValue(
            logic.implantReportInputs.reportType as ImplantReportType
          )}
          // options={formattedReportTypes}
          options={logic.reports}
          autoSelect={false}
          disableClearable
          disabled={!logic.isReferringInfoValid}
        />
      )}

      {logic.inputModalOpen && (
        <ImplantReportSenderModal
          assetIds={logic.assetIds}
          inputModalOpen={logic.inputModalOpen}
          formatEnumValue={logic.formatEnumValue}
          errorMessage={logic.errorMessage}
          surgeryDate={surgeryDate}
          surgeryId={surgeryId}
          implantReportStatus={implantReportStatus}
          implantReportInputs={logic.implantReportInputs}
          setImplantReportInputs={logic.setImplantReportInputs}
          unformatEnumValue={logic.unFormatEnumValue}
          setErrorMessage={logic.setErrorMessage}
          setInputModalOpen={logic.setInputModalOpen}
        />
      )}

      {logic.viewReportsModalOpen && (
        <ViewImplantReportModal
          implantReportStatus={implantReportStatus || []}
          formatEnumValue={logic.formatEnumValue}
          viewReportsModalOpen={logic.viewReportsModalOpen}
          setViewReportsModalOpen={logic.setViewReportsModalOpen}
        />
      )}

      <CustomModal
        open={logic.openModal.open}
        handleOnSubmit={() => {
          logic.handleAddPhysician()
        }}
        icon={<></>}
        header={logic.modalHeader}
        isLoadingState={logic.isAdding || logic.isEditing}
        secondaryButtonAction={logic.handleCloseModal}
        // primaryButtonDisabled={!isSubmitEnabled}
      >
        <TextInput
          disabled={logic.isLoading || logic.disableEdit}
          id="referringFirstName"
          label="Physician First Name"
          name="firstName"
          variant="outlined"
          value={logic.updatedPhysicianDetails?.firstName}
          fullWidth
          handleChangeFormData={logic.handleChangeUpdatedPhysicianDetails}
          errorMessage={logic.formErrors?.firstName}
          error={!!logic.formErrors?.firstName}
        />

        <TextInput
          disabled={logic.isLoading || logic.disableEdit}
          id="referringLastName"
          label="Physician Last Name"
          name="lastName"
          variant="outlined"
          value={logic.updatedPhysicianDetails?.lastName}
          fullWidth
          handleChangeFormData={logic.handleChangeUpdatedPhysicianDetails}
          errorMessage={logic.formErrors?.lastName}
          error={!!logic.formErrors?.lastName}
        />

        <TextInput
          disabled={logic.isLoading}
          id="referringEmail"
          label="Physician Email"
          name="email"
          variant="outlined"
          value={logic.updatedPhysicianDetails?.email}
          handleChangeFormData={logic.handleChangeUpdatedPhysicianDetails}
          fullWidth
          validateEmail
          errorMessage={logic.formErrors?.email}
          error={!!logic.formErrors?.email}
          onPaste={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
        />

        {logic.openModal.type === 'edit'
          ? logic.updatedPhysicianDetails?.email !==
              logic.physicianDetails?.email && (
              <TextInput
                disabled={logic.isLoading}
                id="confirmEmail"
                label="Confirm Email"
                name="confirmEmail"
                variant="outlined"
                value={logic.updatedPhysicianDetails?.confirmEmail}
                handleChangeFormData={logic.handleChangeUpdatedPhysicianDetails}
                fullWidth
                validateEmail
                errorMessage={logic.formErrors?.confirmEmail}
                error={!!logic.formErrors?.confirmEmail}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            )
          : logic.updatedPhysicianDetails?.email && (
              <TextInput
                disabled={logic.isLoading}
                id="confirmEmail"
                label="Confirm Email"
                name="confirmEmail"
                variant="outlined"
                value={logic.updatedPhysicianDetails.confirmEmail}
                handleChangeFormData={logic.handleChangeUpdatedPhysicianDetails}
                fullWidth
                validateEmail
                errorMessage={logic.formErrors?.confirmEmail}
                error={!!logic.formErrors?.confirmEmail}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            )}
      </CustomModal>
    </div>
  )
}
